import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { isEditableElementFocused, isMacPlatform } from '@/lib/utils/platform';

export interface NavShortcut {
  key: string;
  path: string;
  alwaysVisible?: boolean;
}

/**
 * Hook to manage navigation keyboard shortcuts with Ctrl+⌘ modifier reveal.
 *
 * - Pipeline shortcuts (alwaysVisible: true): fire on ⌘+digit (metaKey + digit on mac)
 * - Other shortcuts: require Ctrl+⌘+letter (both ctrlKey and metaKey on mac,
 *   both ctrlKey pressed — approximated as ctrlKey+altKey on non-mac where
 *   there is no metaKey for non-mac, we use ctrlKey only, see note below)
 * - Modifier reveal: exposes isModifierHeld when Ctrl+⌘ (mac) or Ctrl (non-mac)
 *   is held, without calling preventDefault on the modifier keys themselves.
 * - Input guard: no shortcut handling when an input/textarea/contenteditable is focused.
 * - Resets isModifierHeld on: window blur, Escape press.
 *
 * @param shortcuts - Map of shortcut id → { key, path, alwaysVisible }
 * @param orgId - Org ID used to build full paths (prepended as /<orgId>/<path>)
 */
export function useNavShortcuts(
  shortcuts: Record<string, NavShortcut>,
  orgId: string
): { isModifierHeld: boolean } {
  const [isModifierHeld, setIsModifierHeld] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const isMac = isMacPlatform();

    /**
     * Returns true when the "reveal" modifier combo is held.
     * Mac: Ctrl + ⌘ (both ctrlKey and metaKey)
     * Non-mac: Ctrl only (no metaKey available)
     */
    function isRevealModifier(event: KeyboardEvent): boolean {
      if (isMac) {
        return event.ctrlKey && event.metaKey;
      }
      return event.ctrlKey;
    }

    /**
     * Returns true when a pipeline shortcut modifier is held.
     * Mac: ⌘ (metaKey)
     * Non-mac: Ctrl
     */
    function isPipelineModifier(event: KeyboardEvent): boolean {
      if (isMac) {
        return event.metaKey;
      }
      return event.ctrlKey;
    }

    // Capture-phase listener — only tracks modifier state, never prevents default.
    const handleKeyDownCapture = (event: KeyboardEvent) => {
      // Escape resets modifier reveal
      if (event.key === 'Escape') {
        setIsModifierHeld(false);
        return;
      }

      if (isRevealModifier(event)) {
        setIsModifierHeld(true);
      }
    };

    const handleKeyUpCapture = (event: KeyboardEvent) => {
      // If either modifier is released, stop revealing
      if (isMac) {
        if (!event.ctrlKey || !event.metaKey) {
          setIsModifierHeld(false);
        }
      } else {
        if (!event.ctrlKey) {
          setIsModifierHeld(false);
        }
      }
    };

    // Bubble-phase listener — handles actual navigation shortcuts.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) {
        return;
      }

      // Skip when typing in an input
      if (isEditableElementFocused()) {
        setIsModifierHeld(false);
        return;
      }

      // Escape resets state (already done in capture, but also reset here)
      if (event.key === 'Escape') {
        setIsModifierHeld(false);
        return;
      }

      for (const shortcut of Object.values(shortcuts)) {
        if (shortcut.alwaysVisible) {
          // Pipeline shortcuts: ⌘+digit (or Ctrl+digit on non-mac)
          if (isPipelineModifier(event) && event.key === shortcut.key) {
            event.preventDefault();
            navigate(`/${orgId}/${shortcut.path}`);
            return;
          }
        } else {
          // Navigation shortcuts: Ctrl+⌘+letter (or Ctrl+letter on non-mac)
          if (
            isRevealModifier(event) &&
            event.key.toLowerCase() === shortcut.key.toLowerCase()
          ) {
            event.preventDefault();
            navigate(`/${orgId}/${shortcut.path}`);
            return;
          }
        }
      }
    };

    const handleBlur = () => {
      setIsModifierHeld(false);
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (isEditableElementFocused(event.target as Element | null)) {
        setIsModifierHeld(false);
      }
    };

    window.addEventListener('keydown', handleKeyDownCapture, { capture: true });
    window.addEventListener('keyup', handleKeyUpCapture, { capture: true });
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focusin', handleFocusIn);

    return () => {
      window.removeEventListener('keydown', handleKeyDownCapture, {
        capture: true,
      });
      window.removeEventListener('keyup', handleKeyUpCapture, {
        capture: true,
      });
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focusin', handleFocusIn);
    };
  }, [shortcuts, orgId, navigate]);

  return { isModifierHeld };
}
